<template>
  <Teleport to="body">
    <!-- Backdrop -->
    <Transition name="fade">
      <div v-if="open" class="fixed inset-0 z-40 bg-black/20 backdrop-blur-[1px]" @click="$emit('close')"></div>
    </Transition>

    <!-- Panel: 85% of the viewport width at sm+, per the design brief (80-90%) -->
    <Transition name="slide-over">
      <div v-if="open" class="fixed inset-y-0 right-0 z-50 w-full sm:w-[85%] bg-white dark:bg-[#1C2128] shadow-2xl flex flex-col">
        <div class="flex-shrink-0 flex items-center justify-between gap-3 px-5 sm:px-6 py-4 border-b border-gray-100 dark:border-gray-700">
          <div class="min-w-0">
            <h2 class="text-base font-semibold text-gray-900 dark:text-white truncate">{{ title }}</h2>
            <p class="text-xs text-gray-400 dark:text-gray-500 truncate">Catalog entries are shared across every organization</p>
          </div>

          <div class="flex items-center gap-2 flex-shrink-0">
            <button type="button" @click="$emit('close')"
              class="px-4 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors">
              Cancel
            </button>
            <button type="button" @click="submit" :disabled="saving || !name.trim() || !type || !contextType"
              class="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors">
              <svg v-if="saving" class="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
              </svg>
              {{ saving ? 'Saving...' : submitLabel }}
            </button>
          </div>
        </div>

        <!-- Two panes at sm+: form on the left at a fixed, readable width;
             the right pane is a live preview + versions manager. Collapses to
             a single column (form only) below sm. -->
        <div class="flex-1 min-h-0 flex flex-col sm:flex-row">
          <div class="w-full sm:w-[420px] sm:flex-shrink-0 overflow-y-auto px-5 sm:px-6 py-6 space-y-6 sm:border-r sm:border-gray-100 dark:sm:border-gray-700">

            <!-- Logo (first field, per the design brief) — reuses the same
                 upload card as the factsheet's application icon and the
                 settings profile picture, rather than a bespoke uploader. -->
            <div>
              <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">Logo <span class="text-gray-400 dark:text-gray-500 font-normal normal-case">(optional)</span></label>
              <AvatarUploadCard
                :picture-url="initialLogoUrl"
                :avatar-name="avatarInitial"
                avatar-color-class="bg-blue-600"
                :upload-url="`/api/v1/admin/global_components/${globalComponentId}/logo`"
                :remove-url="`/api/v1/admin/global_components/${globalComponentId}/logo`"
                form-field-name="global_component[logo]"
                title="Change catalog logo"
                shape="rounded"
                size="w-14 h-14"
                avatar-text-size="text-base"
                :locked="!locked"
                locked-message="Save the component first to upload a logo"
                @updated="onLogoUpdated"
                @error="onLogoError" />
              <p class="mt-1.5 text-xs text-gray-400 dark:text-gray-500">
                {{ locked ? 'Click the logo to change it.' : 'Falls back to a name-matched catalog icon, or an initial, until saved.' }}
              </p>
            </div>

            <!-- Name -->
            <div>
              <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">Name<span class="text-red-500">*</span></label>
              <input ref="nameInput" v-model="name" type="text" placeholder="e.g. PostgreSQL" autocomplete="off"
                :class="error ? 'border-red-400 dark:border-red-500' : 'border-gray-200 dark:border-gray-700'"
                class="w-full px-3.5 py-2.5 text-sm border rounded-lg bg-white dark:bg-[#0d1117] text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow" />
              <p v-if="error" class="mt-1.5 text-xs text-red-500 dark:text-red-400 font-medium">{{ error }}</p>
            </div>

            <!-- Category (context_type) -->
            <div>
              <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">Category<span v-if="!locked" class="text-red-500">*</span></label>

              <!-- Locked once saved — which context a catalog entry belongs to can't change afterwards. -->
              <div v-if="locked" class="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium bg-gray-50 dark:bg-[#161b22] border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300">
                {{ contextTypeLabel(contextType) }}
              </div>
              <div v-else class="grid grid-cols-2 gap-2">
                <button v-for="opt in CONTEXT_TYPE_OPTIONS" :key="opt.value" type="button" @click="contextType = opt.value"
                  class="px-3 py-2 text-sm font-medium rounded-lg border transition-colors"
                  :class="contextType === opt.value
                    ? 'bg-blue-600 border-blue-600 text-white'
                    : 'bg-white dark:bg-[#0d1117] border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'">
                  {{ opt.label }}
                </button>
              </div>
              <p class="mt-1.5 text-xs text-gray-400 dark:text-gray-500">{{ locked ? "Can't be changed once created." : 'Which catalog this entry belongs to.' }}</p>
            </div>

            <!-- Status: only meaningful once the entry actually exists — a
                 fresh Add is always created pre-approved server-side. Fires
                 immediately on select (a separate approve/reject/unapprove
                 endpoint), same as the version-level Status field. This is
                 now the only place status can be changed — the catalog
                 table/cards show it read-only. -->
            <div v-if="locked">
              <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">Status</label>
              <select :value="status" @change="$emit('change-status', $event.target.value)" :disabled="statusWorking"
                class="w-full px-3.5 py-2.5 text-sm font-semibold rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 transition"
                :class="statusSelectClass(status)">
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option v-if="contextType === 'Application'" value="org_specific">Org specific</option>
              </select>
              <p class="mt-1.5 text-xs text-gray-400 dark:text-gray-500">
                {{ contextType === 'Application'
                  ? 'Approve or reject to moderate the shared catalog, or mark org specific if this belongs to just one org.'
                  : 'Approve or reject to moderate the shared catalog.' }}
              </p>
            </div>

            <!-- Type -->
            <div>
              <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">Type<span class="text-red-500">*</span></label>
              <select v-model="type"
                class="w-full px-3.5 py-2.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-[#0d1117] text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow">
                <option value="" disabled>Select a type</option>
                <option v-for="option in TYPE_OPTIONS" :key="option.value" :value="option.value">{{ option.label }}</option>
              </select>
              <p class="mt-1.5 text-xs text-gray-400 dark:text-gray-500">Categorizes what kind of component this is.</p>
            </div>

            <!-- Vendor -->
            <div>
              <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">Vendor <span class="text-gray-400 dark:text-gray-500 font-normal normal-case">(optional)</span></label>
              <input v-model="vendor" type="text" placeholder="e.g. PostgreSQL Global Development Group"
                class="w-full px-3.5 py-2.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-[#0d1117] text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow" />
              <p class="mt-1.5 text-xs text-gray-400 dark:text-gray-500">Who builds or maintains this component.</p>
            </div>

            <!-- Description -->
            <div>
              <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">Description <span class="text-gray-400 dark:text-gray-500 font-normal normal-case">(optional)</span></label>
              <textarea v-model="description" rows="3" placeholder="What is this component, briefly?"
                class="w-full px-3.5 py-2.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-[#0d1117] text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow resize-none"></textarea>
            </div>
          </div>

          <!-- Right pane: live preview on top, versions manager below. Hidden
               below sm — the form alone already fills a full-width mobile drawer. -->
          <div class="hidden sm:flex flex-1 flex-col overflow-y-auto bg-gray-50 dark:bg-[#0d1117] px-6 py-6 space-y-4">
            <div>
              <p class="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-3">Preview</p>

              <div v-if="name.trim()" class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 p-5">
                <div class="flex items-start gap-3">
                  <ItComponentLogo :name="name.trim()" :logo-url="initialLogoUrl" size="w-11 h-11" initial-text-size="text-sm" />
                  <div class="min-w-0 flex-1 pt-0.5">
                    <div class="flex items-center gap-2 flex-wrap">
                      <h3 class="text-base font-semibold text-gray-900 dark:text-gray-100 truncate">{{ name.trim() }}</h3>
                      <span v-if="contextType" class="inline-flex flex-shrink-0 items-center px-2 py-0.5 rounded-full text-[11px] font-medium" :class="contextTypeBadgeClass(contextType)">
                        {{ contextTypeLabel(contextType) }}
                      </span>
                      <span v-if="type" class="inline-flex flex-shrink-0 items-center px-2 py-0.5 rounded-full text-[11px] font-medium" :class="typeBadgeClass(type)">
                        {{ typeLabel(type) }}
                      </span>
                      <span v-else class="inline-flex flex-shrink-0 items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500">
                        No type
                      </span>
                    </div>
                    <p class="text-xs text-gray-400 dark:text-gray-500 truncate mt-0.5">{{ vendor.trim() || 'No vendor listed' }}</p>
                    <p v-if="description.trim()" class="text-xs text-gray-500 dark:text-gray-400 mt-1.5 line-clamp-2">{{ description.trim() }}</p>
                  </div>
                </div>
              </div>

              <div v-else class="flex flex-col items-center justify-center text-center py-12 bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700">
                <div class="w-12 h-12 rounded-xl bg-gray-50 dark:bg-[#161b22] border border-gray-100 dark:border-gray-700 flex items-center justify-center mb-3">
                  <svg class="w-5 h-5 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M21.75 17.25v-.228a4.5 4.5 0 0 0-.12-1.03l-2.268-9.64a3.375 3.375 0 0 0-3.285-2.602H7.923a3.375 3.375 0 0 0-3.285 2.602l-2.268 9.64a4.5 4.5 0 0 0-.12 1.03v.228m19.5 0a3 3 0 0 1-3 3H5.25a3 3 0 0 1-3-3m19.5 0a3 3 0 0 0-3-3H5.25a3 3 0 0 0-3 3m16.5 0h.008v.008h-.008v-.008Zm-3 0h.008v.008h-.008v-.008Z"/>
                  </svg>
                </div>
                <p class="text-sm font-medium text-gray-400 dark:text-gray-500">Nothing to preview yet</p>
                <p class="text-xs text-gray-400 dark:text-gray-500 mt-1 max-w-[220px]">Fill in a name on the left to see a preview here.</p>
              </div>
            </div>

            <!-- Versions: shown as soon as a name is picked, even before the
                 first Save — GlobalComponentVersionsSection renders its own
                 disabled preview until globalComponentId is real. -->
            <div v-if="name.trim()">
              <GlobalComponentVersionsSection :global-component-id="globalComponentId" :highlight="highlightVersions"
                @notify="(message, type) => emit('notify', message, type)"
                @version-status-changed="(payload) => emit('version-status-changed', payload)" />
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch, onMounted, nextTick } from 'vue'
import {
  TYPE_OPTIONS, typeLabel, typeBadgeClass,
  CONTEXT_TYPE_OPTIONS, contextTypeLabel, contextTypeBadgeClass,
} from 'controllers/vue_components/organizations/it_components/itComponentOptions.js'
import ItComponentLogo from 'controllers/vue_components/organizations/it_components/ItComponentLogo.vue'
import AvatarUploadCard from 'controllers/vue_components/settings/shared/AvatarUploadCard.vue'
import GlobalComponentVersionsSection from 'controllers/vue_components/admin/global_components/GlobalComponentVersionsSection.vue'

const props = defineProps({
  // The component stays mounted between opens (like BookingSlideOver) so the
  // backdrop fade and panel slide can both animate in AND out.
  open: { type: Boolean, default: false },
  title: { type: String, required: true },
  submitLabel: { type: String, default: 'Add' },
  saving: { type: Boolean, default: false },
  initialName: { type: String, default: '' },
  initialType: { type: String, default: '' },
  initialVendor: { type: String, default: '' },
  initialDescription: { type: String, default: '' },
  initialContextType: { type: String, default: 'ItComponent' },
  initialLogoUrl: { type: String, default: '' },
  // Only set when editing an already-saved component — gates the Versions
  // section, unlocks the logo uploader (it needs a real id to attach to),
  // and locks the Category field (see the template).
  globalComponentId: { type: String, default: '' },
  // Moderation status — only shown once locked (see the template). Changes
  // fire immediately on select (a separate approve/reject/unapprove
  // endpoint), not bundled into the regular Save.
  status: { type: String, default: 'pending' },
  statusWorking: { type: Boolean, default: false },
  // True for the brief window right after this component was just created —
  // draws attention to the Versions section that only just became usable.
  highlightVersions: { type: Boolean, default: false },
  error: { type: String, default: '' },
})

const locked = computed(() => !!props.globalComponentId)

const emit = defineEmits(['close', 'submit', 'notify', 'logo-updated', 'change-status', 'version-status-changed'])

const STATUS_SELECT_CLASSES = {
  pending: 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 border-amber-200 dark:border-amber-800',
  approved: 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800',
  rejected: 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 border-red-200 dark:border-red-800',
  org_specific: 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 border-purple-200 dark:border-purple-800',
}
function statusSelectClass(status) {
  return STATUS_SELECT_CLASSES[status] ?? 'bg-gray-100 dark:bg-gray-800 text-gray-500 border-gray-200 dark:border-gray-700'
}

const name = ref(props.initialName)
const type = ref(props.initialType)
const vendor = ref(props.initialVendor)
const description = ref(props.initialDescription)
const contextType = ref(props.initialContextType)
const nameInput = ref(null)

const avatarInitial = computed(() => name.value.trim()?.[0]?.toUpperCase() || '?')

// AvatarUploadCard uploads/removes/purges directly against the logo
// endpoints itself (see its own uploadUrl/removeUrl props above) — it isn't
// routed through the admin_global_components store. The fresh record it
// reports back replaces the caller's cached one so logo_url stays in sync.
function onLogoUpdated(updatedComponent) {
  emit('logo-updated', updatedComponent)
}

function onLogoError(message) {
  emit('notify', message, 'alert')
}

// Reset/populate the form every time the panel opens — the component itself
// persists across opens, only its props (which record is being edited, if
// any) change, so this can't rely on a fresh mount the way a modal would.
watch(() => props.open, (isOpen) => {
  if (!isOpen) return
  name.value = props.initialName
  type.value = props.initialType
  vendor.value = props.initialVendor
  description.value = props.initialDescription
  contextType.value = props.initialContextType
  if (!props.initialName) nextTick(() => nameInput.value?.focus())
})

function submit() {
  if (!name.value.trim() || !type.value || !contextType.value) return

  emit('submit', {
    name: name.value.trim(),
    type: type.value,
    vendor: vendor.value.trim(),
    description: description.value.trim(),
    context_type: contextType.value,
  })
}

onMounted(() => {
  if (!props.initialName) nextTick(() => nameInput.value?.focus())
})
</script>

<style scoped>
.slide-over-enter-active { transition: transform 0.25s ease; }
.slide-over-leave-active { transition: transform 0.20s ease; }
.slide-over-enter-from,
.slide-over-leave-to    { transform: translateX(100%); }

.fade-enter-active { transition: opacity 0.2s ease; }
.fade-leave-active { transition: opacity 0.15s ease; }
.fade-enter-from,
.fade-leave-to    { opacity: 0; }
</style>
